import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { unwrapPage } from "@/lib/paging";
import { toQuery } from "@/lib/filters";
import { formatDateTime } from "@/lib/dateTime";
import { cn } from "@/lib/utils";
import { useToast } from "@/context/ToastContext";
import { MoveSelect } from "@/components/boards/MoveSelect";
import { PreviewButton } from "@/components/preview/PreviewButton";

interface BoardLead {
  id: string;
  first_name: string;
  last_name?: string;
  company_name?: string;
  priority?: string;
  lead_status: string;
  next_follow_up_at?: string | null;
  owner?: { first_name: string; last_name?: string } | null;
}

const COLUMNS = [
  { key: "NEW", label: "New" },
  { key: "CONTACTED", label: "Contacted" },
  { key: "FOLLOW_UP", label: "Follow-up" },
  { key: "QUALIFIED", label: "Qualified" },
  { key: "DISQUALIFIED", label: "Disqualified" },
  { key: "CONVERTED", label: "Converted" },
];
/** The moves the server allows from a board; disqualifying and converting need their reason / account, so they stay on the lead's page */
const MOVES: Record<string, string[]> = { NEW: ["CONTACTED"], CONTACTED: ["QUALIFIED"], FOLLOW_UP: ["QUALIFIED"], DISQUALIFIED: ["NEW"], QUALIFIED: [], CONVERTED: [] };
const PRIORITY_TONE: Record<string, string> = { HOT: "bg-danger/15 text-danger", WARM: "bg-warning/15 text-warning", COLD: "bg-muted text-muted-foreground" };
const PAGE = 20;

interface Col {
  rows: BoardLead[];
  total: number;
  page: number;
  loading: boolean;
}

/**
 * The leads as a board, one column per status, under the same filters, search and sort as the table. Drag a card to the next
 * status (New → Contacted → Qualified, or re-open a disqualified one); the server applies its usual transition rules.
 */
export function LeadsBoard({ query, canMove, onChanged }: { query: Record<string, string | number | null | undefined>; canMove: boolean; onChanged: () => void }) {
  const { error, success } = useToast();
  const [cols, setCols] = useState<Record<string, Col>>({});
  const [drag, setDrag] = useState<{ id: string; from: string } | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const key = JSON.stringify(query);
  const seq = useRef(0);

  const fetchCol = useCallback(
    async (status: string, page: number, token: number) => {
      setCols((c) => ({ ...c, [status]: { rows: c[status]?.rows || [], total: c[status]?.total || 0, page, loading: true } }));
      try {
        const res = unwrapPage<BoardLead>(await api.get(`/api/Lead/List${toQuery({ ...query, status, page, page_size: PAGE })}`));
        if (token !== seq.current) return;
        setCols((c) => ({ ...c, [status]: { rows: page === 1 ? res.rows : [...(c[status]?.rows || []), ...res.rows], total: res.total, page, loading: false } }));
      } catch (e: any) {
        if (token !== seq.current) return;
        setCols((c) => ({ ...c, [status]: { ...(c[status] || { rows: [], total: 0 }), page, loading: false } }));
        error("Couldn't load the board", e?.message || "Try again");
      }
    },
    [key], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const reloadAll = useCallback(() => {
    const token = ++seq.current;
    COLUMNS.forEach((c) => void fetchCol(c.key, 1, token));
  }, [fetchCol]);

  useEffect(() => {
    reloadAll();
  }, [reloadAll]);

  const drop = (to: string) => {
    const d = drag;
    setDrag(null);
    setOver(null);
    if (d) void move(d.id, d.from, to);
  };

  const move = async (id: string, from: string, to: string) => {
    const d = { id, from };
    if (from === to) return;
    if (!MOVES[d.from]?.includes(to)) {
      error("Can't move it there", to === "FOLLOW_UP" && d.from === "CONTACTED" ? "Open the lead to pick the date of the follow-up." : to === "DISQUALIFIED" ? "Open the lead to disqualify it with a reason." : to === "CONVERTED" ? "Open the lead to convert it to an account and deal." : `A ${d.from.toLowerCase().replace("_", "-")} lead can't go straight to ${to.toLowerCase().replace("_", "-")}.`);
      return;
    }
    try {
      await api.patch(`/api/Lead/ChangeStatus/${d.id}`, { status: to });
      success("Lead moved", `Now ${to.toLowerCase()}.`);
      reloadAll();
      onChanged();
    } catch (e: any) {
      error("Couldn't move the lead", e?.message || "Try again");
      reloadAll();
    }
  };

  return (
    <div className="flex items-start gap-2.5 overflow-x-auto pb-4" aria-label="Leads board">
      {COLUMNS.map((c) => {
        const col = cols[c.key];
        const target = !!drag && drag.from !== c.key && MOVES[drag.from]?.includes(c.key);
        return (
          <section
            key={c.key}
            aria-label={c.label}
            onDragOver={(e) => {
              if (!drag) return;
              e.preventDefault();
              if (over !== c.key) setOver(c.key);
            }}
            onDragLeave={(e) => !e.currentTarget.contains(e.relatedTarget as Node) && setOver(null)}
            onDrop={(e) => {
              e.preventDefault();
              void drop(c.key);
            }}
            className={cn("w-[270px] shrink-0 rounded-lg border border-border bg-panel p-2", over === c.key && (target ? "border-primary ring-2 ring-primary/30" : "border-destructive/50"))}
          >
            <header className="mb-2 flex items-center justify-between px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <span>{c.label}</span>
              <span className="font-mono text-[11.5px]">{col ? col.total : "…"}</span>
            </header>
            <div className="space-y-2">
              {(col?.rows || []).map((l) => (
                <article
                  key={l.id}
                  draggable={canMove && (MOVES[l.lead_status]?.length || 0) > 0}
                  onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = "move";
                    e.dataTransfer.setData("text/plain", l.id);
                    setDrag({ id: l.id, from: c.key });
                  }}
                  onDragEnd={() => (setDrag(null), setOver(null))}
                  className={cn("group/row group/card rounded-md border border-border bg-card p-2.5 text-sm shadow-xs", canMove && MOVES[l.lead_status]?.length ? "cursor-grab active:cursor-grabbing" : "", drag?.id === l.id && "opacity-40")}
                >
                  <div className="flex items-start justify-between gap-1">
                    <Link to={`/leads/${l.id}`} className="min-w-0 truncate font-semibold hover:text-primary hover:underline">
                      {l.first_name} {l.last_name || ""}
                    </Link>
                    <PreviewButton entity="Lead" id={l.id} label={`${l.first_name} ${l.last_name || ""}`.trim()} />
                  </div>
                  {l.company_name && <div className="truncate text-xs text-muted-foreground">{l.company_name}</div>}
                  <div className="mt-1.5 flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
                    {l.priority && <span className={cn("rounded px-1.5 py-0.5 font-medium", PRIORITY_TONE[l.priority] || "bg-muted")}>{l.priority.charAt(0) + l.priority.slice(1).toLowerCase()}</span>}
                    {l.next_follow_up_at && (
                      <span className="inline-flex items-center gap-1">
                        <CalendarClock className="size-3" />
                        {formatDateTime(l.next_follow_up_at)}
                      </span>
                    )}
                  </div>
                  {l.owner && <div className="mt-1 truncate text-[11px] text-muted-foreground">{`${l.owner.first_name} ${l.owner.last_name || ""}`.trim()}</div>}
                  {canMove && (
                    <MoveSelect
                      label={`Move ${l.first_name} to another status`}
                      className="mt-1.5"
                      options={(MOVES[l.lead_status] || []).map((s) => ({ value: s, label: COLUMNS.find((x) => x.key === s)?.label || s }))}
                      onPick={(to) => void move(l.id, l.lead_status, to)}
                    />
                  )}
                </article>
              ))}
              {col && !col.loading && col.rows.length === 0 && <p className="px-1 py-3 text-center text-xs text-muted-foreground">No leads</p>}
              {col?.loading && <Loader2 className="mx-auto my-2 size-4 animate-spin text-muted-foreground" />}
              {col && !col.loading && col.rows.length < col.total && (
                <button type="button" onClick={() => void fetchCol(c.key, col.page + 1, seq.current)} className="h-7 w-full rounded-md text-xs font-medium text-primary hover:bg-muted">
                  Show more ({col.total - col.rows.length})
                </button>
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}
